import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'hooks';
import type { APIAccountHolders, APIAdditionalInsured } from 'types/api.types';

interface Applicant {
  email?: string;
  name?: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
}

interface UseAccountHoldersProps {
  accountHolders: APIAccountHolders;
  applicant: Applicant | undefined;
  coApplicant: Applicant | undefined;
  actions: any;
  additionalInsureds?: APIAdditionalInsured[];
}

export const useAccountHolders = ({
  accountHolders,
  applicant,
  coApplicant,
  actions,
  additionalInsureds = [],
}: UseAccountHoldersProps) => {
  const dispatch = useAppDispatch();
  const [showSecondAccountHolder, setShowSecondAccountHolder] = useState(false);

  // Check if any second account holder field has a value
  const hasSecondAccountHolderData = () => {
    return !!(
      accountHolders?.second?.email ||
      accountHolders?.second?.first_name ||
      accountHolders?.second?.middle_name ||
      accountHolders?.second?.last_name ||
      accountHolders?.second?.address ||
      accountHolders?.second?.city ||
      accountHolders?.second?.province_code ||
      accountHolders?.second?.postal_code ||
      accountHolders?.second?.phone
    );
  };

  // Show second account holder fields on mount if any field has data
  useEffect(() => {
    if (hasSecondAccountHolderData()) {
      setShowSecondAccountHolder(true);
    }
  }, [accountHolders?.second]);

  // Email validation functions
  const validateFirstAccountHolderEmail = () => {
    if (
      !accountHolders?.first?.email ||
      accountHolders?.first?.is_same_as_applicant
    ) {
      return null;
    }

    const firstAccountHolderEmail = accountHolders.first.email
      .toLowerCase()
      .trim();

    const secondAccountHolderEmail = accountHolders?.second?.email
      ?.toLowerCase()
      .trim();

    if (
      secondAccountHolderEmail &&
      !accountHolders?.second?.is_same_as_co_applicant &&
      firstAccountHolderEmail === secondAccountHolderEmail
    ) {
      return 'E-signing will not be available if both account holders use the same email';
    }

    return null;
  };

  const validateSecondAccountHolderEmail = () => {
    if (!accountHolders?.second?.email) {
      return null;
    }

    const secondAccountHolderEmail = accountHolders.second.email
      .toLowerCase()
      .trim();
    const firstAccountHolderEmail = accountHolders?.first?.email
      ?.toLowerCase()
      .trim();

    if (
      firstAccountHolderEmail &&
      secondAccountHolderEmail === firstAccountHolderEmail
    ) {
      return 'E-signing will not be available if both account holders use the same email';
    }

    return null;
  };

  // Get current selection value for first holder radio group
  const getFirstHolderSelectionValue = () => {
    if (accountHolders?.first?.is_same_as_applicant) {
      return 'applicant';
    }
    if (accountHolders?.first?.is_same_as_co_applicant) {
      return 'co_applicant';
    }
    return 'other';
  };

  // Check if second account holder matches co-applicant (for radio state)
  const isSecondHolderSameAsCoApplicant = useMemo(() => {
    if (!coApplicant || !accountHolders?.second) return false;

    const holderEmail = accountHolders.second.email?.toLowerCase().trim();
    const coApplicantEmail = coApplicant.email?.toLowerCase().trim();

    if (accountHolders.second.is_same_as_co_applicant) return true;

    return holderEmail && coApplicantEmail && holderEmail === coApplicantEmail;
  }, [coApplicant, accountHolders?.second]);

  // Get current selection value for second holder radio group
  const getSecondHolderSelectionValue = () => {
    if (!showSecondAccountHolder) {
      return 'none';
    }
    if (isSecondHolderSameAsCoApplicant) {
      return 'co_applicant';
    }
    return 'other';
  };

  // Check if co-applicant exists
  const hasCoApplicant =
    coApplicant &&
    (coApplicant.email ||
      ('name' in coApplicant && coApplicant.name) ||
      ('first_name' in coApplicant && coApplicant.first_name));

  // Get first holder options
  const getFirstHolderOptions = () => {
    if (hasCoApplicant) {
      return [
        { value: 'applicant', label: 'Same as Applicant' },
        { value: 'co_applicant', label: 'Same as Co-Applicant' },
        { value: 'other', label: 'Other' },
      ];
    }
    return [
      { value: 'applicant', label: 'Same as Applicant' },
      { value: 'other', label: 'Other' },
    ];
  };

  // Get second holder options based on first holder selection
  const getSecondHolderOptions = () => {
    const firstHolderValue = getFirstHolderSelectionValue();
    const options = [{ value: 'none', label: 'None' }];

    if (
      (firstHolderValue === 'applicant' || firstHolderValue === 'other') &&
      hasCoApplicant
    ) {
      options.push({ value: 'co_applicant', label: 'Same as Co-Applicant' });
    }

    options.push({ value: 'other', label: 'Other' });

    return options;
  };

  // Handler for first holder selection change
  const handleFirstHolderSelectionChange = (value: string) => {
    if (value === 'applicant') {
      dispatch(
        actions.changeAccountHolder({
          holder: 'first',
          name: 'is_same_as_applicant',
          value: true,
        }),
      );
    } else if (value === 'co_applicant' && coApplicant) {
      dispatch(
        actions.changeAccountHolder({
          holder: 'first',
          name: 'is_same_as_co_applicant',
          value: true,
        }),
      );
    } else {
      dispatch(
        actions.changeAccountHolder({
          holder: 'first',
          name: 'is_same_as_applicant',
          value: false,
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'first',
          name: 'is_same_as_co_applicant',
          value: false,
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'first',
          name: 'email',
          value: '',
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'first',
          name: 'first_name',
          value: '',
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'first',
          name: 'middle_name',
          value: '',
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'first',
          name: 'last_name',
          value: '',
        }),
      );
    }
  };

  // Helper to extract name parts from applicant
  const extractNameParts = (person: Applicant) => {
    let firstName = '';
    let middleName = '';
    let lastName = '';

    if ('name' in person && person.name) {
      const nameParts = (person.name || '').trim().split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    } else if ('first_name' in person) {
      firstName = person.first_name || '';
      middleName = person.middle_name || '';
      lastName = person.last_name || '';
    }

    return { firstName, middleName, lastName };
  };

  // Handler for second holder selection change
  const handleSecondHolderSelectionChange = (value: string) => {
    if (value === 'none') {
      setShowSecondAccountHolder(false);
      const clearFields = [
        'email',
        'first_name',
        'middle_name',
        'last_name',
        'address',
        'city',
        'province_code',
        'postal_code',
        'phone',
      ];
      clearFields.forEach((field) => {
        dispatch(
          actions.changeAccountHolder({
            holder: 'second',
            name: field,
            value: '',
          }),
        );
      });
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'is_same_as_co_applicant',
          value: false,
        }),
      );
    } else if (value === 'co_applicant' && coApplicant) {
      setShowSecondAccountHolder(true);
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'is_same_as_co_applicant',
          value: true,
        }),
      );

      const { firstName, middleName, lastName } = extractNameParts(coApplicant);

      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'email',
          value: coApplicant.email || '',
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'first_name',
          value: firstName,
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'middle_name',
          value: middleName,
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'last_name',
          value: lastName,
        }),
      );
    } else {
      setShowSecondAccountHolder(true);
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'is_same_as_co_applicant',
          value: false,
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'email',
          value: '',
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'first_name',
          value: '',
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'middle_name',
          value: '',
        }),
      );
      dispatch(
        actions.changeAccountHolder({
          holder: 'second',
          name: 'last_name',
          value: '',
        }),
      );
    }
  };

  const validateAdditionalInsuredEmail = (index: number): string | null => {
    const insured = additionalInsureds[index];
    if (!insured?.email) return null;

    const email = insured.email.toLowerCase().trim();

    const otherEmails = additionalInsureds
      .filter((_, i) => i !== index)
      .map((ins) => ins.email?.toLowerCase().trim())
      .filter(Boolean);

    if (otherEmails.includes(email)) {
      return 'E-signing will not be available if additional insureds share the same email';
    }

    return null;
  };

  return {
    showSecondAccountHolder,
    validateFirstAccountHolderEmail,
    validateSecondAccountHolderEmail,
    validateAdditionalInsuredEmail,
    getFirstHolderSelectionValue,
    getSecondHolderSelectionValue,
    getFirstHolderOptions,
    getSecondHolderOptions,
    handleFirstHolderSelectionChange,
    handleSecondHolderSelectionChange,
    isSecondHolderSameAsCoApplicant,
  };
};
